import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { SupportChatWidget } from "@/components/SupportChatWidget";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { useErrorReporting } from "@/hooks/useErrorReporting";
import { useEffect } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import Home from "./pages/Home";
import Tools from "./pages/Tools";
import Auth from "./pages/Auth";
import Admin from "./pages/Admin";
import NotFound from "./pages/NotFound";
import DecemberEmailDrop from "./pages/insights/DecemberEmailDrop";
import SendingTimeSpamFilters from "./pages/insights/SendingTimeSpamFilters";
import DomainWarmupStrategies from "./pages/insights/DomainWarmupStrategies";
import GmailPromotionsTab from "./pages/insights/GmailPromotionsTab";
import SpfDkimDmarcGuide from "./pages/insights/SpfDkimDmarcGuide";
import ColdEmailDeliverability from "./pages/insights/ColdEmailDeliverability";
import ApiSecurityBestPractices from "./pages/insights/ApiSecurityBestPractices";
import VibeCodingGuide from "./pages/insights/VibeCodingGuide";
import AiCodingAssistants from "./pages/insights/AiCodingAssistants";
import PromptEngineeringDevelopers from "./pages/insights/PromptEngineeringDevelopers";
import NoCodeVsCode from "./pages/insights/NoCodeVsCode";
import AiAgentsAutomation from "./pages/insights/AiAgentsAutomation";
import LlmIntegrationGuide from "./pages/insights/LlmIntegrationGuide";
import PhishingAttackDetection from "./pages/insights/PhishingAttackDetection";
import SecureCodingPractices from "./pages/insights/SecureCodingPractices";
import ProjectManagementComparison from "./pages/insights/ProjectManagementComparison";
import ReplyBasedDeliverability from "./pages/insights/ReplyBasedDeliverability";
import ZeroTrustEmailDmarc from "./pages/insights/ZeroTrustEmailDmarc";
import GeoVsSeo from "./pages/insights/GeoVsSeo";
import ItStakeholderRoles from "./pages/insights/ItStakeholderRoles";
import StartupFailureDelegation from "./pages/insights/StartupFailureDelegation";
import DepartmentalAutonomy from "./pages/insights/DepartmentalAutonomy";
import UpworkFiverrDecline from "./pages/insights/UpworkFiverrDecline";
import MicrosoftDropsClaudeCode from "./pages/insights/MicrosoftDropsClaudeCode";
import AiCostVsEmployees from "./pages/insights/AiCostVsEmployees";
import SummerEmailDrop from "./pages/insights/SummerEmailDrop";
import EmailBlacklistsExplained from "./pages/insights/EmailBlacklistsExplained";
import ToolPage from "./pages/ToolPage";
import Glossary from "./pages/Glossary";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PerSeatPricingTokenEconomics from "./pages/insights/PerSeatPricingTokenEconomics";

const queryClient = new QueryClient();

// Component to handle session tracking, page view tracking, and error reporting
const SessionTracker = () => {
  const location = useLocation();
  const { trackPageView } = useSessionTracking();
  useErrorReporting(); // Set up global error handlers

  useEffect(() => {
    trackPageView();
  }, [location.pathname, trackPageView]);

  return null;
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <SessionTracker />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/tools/:toolId" element={<ToolPage />} />
              <Route path="/insights/december-email-drop" element={<DecemberEmailDrop />} />
              <Route path="/insights/sending-time-spam-filters" element={<SendingTimeSpamFilters />} />
              <Route path="/insights/domain-warmup-strategies" element={<DomainWarmupStrategies />} />
              <Route path="/insights/gmail-promotions-tab" element={<GmailPromotionsTab />} />
              <Route path="/insights/spf-dkim-dmarc-guide" element={<SpfDkimDmarcGuide />} />
              <Route path="/insights/cold-email-deliverability" element={<ColdEmailDeliverability />} />
              <Route path="/insights/api-security-best-practices" element={<ApiSecurityBestPractices />} />
              <Route path="/insights/vibe-coding-guide" element={<VibeCodingGuide />} />
              <Route path="/insights/ai-coding-assistants" element={<AiCodingAssistants />} />
              <Route path="/insights/prompt-engineering-developers" element={<PromptEngineeringDevelopers />} />
              <Route path="/insights/no-code-vs-code" element={<NoCodeVsCode />} />
              <Route path="/insights/ai-agents-automation" element={<AiAgentsAutomation />} />
              <Route path="/insights/llm-integration-guide" element={<LlmIntegrationGuide />} />
              <Route path="/insights/phishing-attack-detection" element={<PhishingAttackDetection />} />
              <Route path="/insights/secure-coding-practices" element={<SecureCodingPractices />} />
              <Route path="/insights/project-management-tools" element={<ProjectManagementComparison />} />
              <Route path="/insights/reply-based-deliverability" element={<ReplyBasedDeliverability />} />
              <Route path="/insights/zero-trust-email-dmarc" element={<ZeroTrustEmailDmarc />} />
              <Route path="/insights/geo-vs-seo" element={<GeoVsSeo />} />
              <Route path="/insights/it-stakeholder-roles" element={<ItStakeholderRoles />} />
              <Route path="/insights/startup-failure-delegation" element={<StartupFailureDelegation />} />
              <Route path="/insights/departmental-autonomy" element={<DepartmentalAutonomy />} />
              <Route path="/insights/upwork-fiverr-decline" element={<UpworkFiverrDecline />} />
              <Route path="/insights/microsoft-drops-claude-code" element={<MicrosoftDropsClaudeCode />} />
              <Route path="/insights/ai-cost-vs-employees" element={<AiCostVsEmployees />} />
              <Route path="/insights/summer-email-drop" element={<SummerEmailDrop />} />
              <Route path="/insights/email-blacklists-explained" element={<EmailBlacklistsExplained />} />
              <Route path="/insights/per-seat-pricing-token-economics" element={<PerSeatPricingTokenEconomics />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/admin" element={<Admin />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsentBanner />
            <SupportChatWidget />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
